/**
 * 学习进度条组件
 * 
 * 使用 SSE 实时订阅学习进度，展示漂亮的细线进度条
 */

import { useEffect, useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { TokenManager } from '@/shared/services/api'

// 学习阶段配置
const LEARNING_STAGES = [
  { id: 'validate', name: '验证', icon: '📋', description: '语料验证 + 敏感词检测' },
  { id: 'extract', name: '提取', icon: '🔧', description: 'LLM 提取结构化数据' },
  { id: 'normalize', name: '标准化', icon: '🔄', description: 'Speaker 归一化、去重' },
  { id: 'analyze', name: '分析', icon: '📊', description: '统计特征 + LLM 推断' },
  { id: 'learn', name: '学习', icon: '🎓', description: '生成 injection_pack' },
  { id: 'save', name: '保存', icon: '💾', description: '存储配置' },
  { id: 'completed', name: '完成', icon: '🎉', description: '学习完成' },
]

interface SSEProgressData {
  type: 'progress' | 'complete' | 'error' | 'timeout'
  stage?: string
  stage_name?: string
  progress: number
  message?: string
  status?: string
  error?: string
  sub_progress?: number
  sub_total?: number
}

interface LearningProgressBarProps {
  identityId: string
  sceneId?: string // Facet 学习时传入
  jobId?: string   // 指定 job_id，避免获取旧任务
  onComplete?: () => void
  onError?: (error: string) => void
  className?: string
}

export function LearningProgressBar({
  identityId,
  sceneId,
  jobId,
  onComplete,
  onError,
  className,
}: LearningProgressBarProps) {
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState('validate')
  const [, setStageName] = useState('📋 验证')
  const [message, setMessage] = useState('准备开始学习...')
  const [subProgress, setSubProgress] = useState<{ current: number; total: number } | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 获取当前阶段索引
  const currentStageIndex = LEARNING_STAGES.findIndex(s => s.id === stage)

  // 订阅 SSE
  useEffect(() => {
    // SSE URL: /api/v1/identity/identities/{id}/learn/stream
    // 或 /api/v1/identity/identities/{id}/facets/{scene}/learn/stream
    // 注意：EventSource 不支持自定义 header，需要通过 query parameter 传递 token
    const baseUrl = '/api/v1/identity'
    const basePath = sceneId
      ? `${baseUrl}/identities/${identityId}/facets/${sceneId}/learn/stream`
      : `${baseUrl}/identities/${identityId}/learn/stream`
    
    // 构建查询参数（token + job_id）
    const accessToken = TokenManager.getToken()
    const params = new URLSearchParams()
    if (accessToken) params.set('token', accessToken)
    if (jobId) params.set('job_id', jobId)
    const queryString = params.toString()
    const url = queryString ? `${basePath}?${queryString}` : basePath
    
    console.log('🔗 [SSE] 连接学习进度流:', url, jobId ? `(job: ${jobId})` : '(latest job)')

    const eventSource = new EventSource(url)

    eventSource.onopen = () => {
      setIsConnected(true)
      setError(null)
    }

    eventSource.onmessage = (event) => {
      try {
        const data: SSEProgressData = JSON.parse(event.data)
        console.log('📊 [SSE] 收到数据:', data)

        // 根据事件类型处理
        if (data.type === 'progress') {
          // 进度更新
          setProgress(data.progress)
          if (data.stage) setStage(data.stage)
          if (data.stage_name) setStageName(data.stage_name)
          if (data.message) setMessage(data.message)

          if (data.sub_progress !== undefined && data.sub_total !== undefined) {
            setSubProgress({ current: data.sub_progress, total: data.sub_total })
          } else {
            setSubProgress(null)
          }

          // 检查 stage 是否完成（兼容旧格式）
          if (data.stage === 'completed') {
            eventSource.close()
            setIsConnected(false)
            setProgress(100)
            setMessage('学习完成！')
            onComplete?.()
          }
        } else if (data.type === 'complete') {
          // 学习完成
          eventSource.close()
          setIsConnected(false)
          setProgress(100)
          setStage('completed')
          setMessage('学习完成！')
          onComplete?.()
        } else if (data.type === 'error' || data.status === 'failed') {
          // 学习失败
          eventSource.close()
          setIsConnected(false)
          const errorMsg = data.error || data.message || '学习失败'
          setError(errorMsg)
          onError?.(errorMsg)
        } else if (data.type === 'timeout') {
          // 连接超时
          eventSource.close()
          setIsConnected(false)
          setError('连接超时，请刷新重试')
          onError?.('连接超时')
        }
      } catch (e) {
        console.error('Failed to parse SSE data:', e)
      }
    }

    eventSource.onerror = () => {
      eventSource.close()
      setIsConnected(false)
      setError('连接中断，请刷新重试')
      onError?.('连接中断')
    }

    return () => {
      eventSource.close()
    }
  }, [identityId, sceneId, jobId, onComplete, onError])

  if (error) {
    return (
      <div className={cn('p-4 bg-red-500/10 border border-red-500/30 rounded-lg', className)}>
        <div className="flex items-center gap-2 text-red-400">
          <span>❌</span>
          <span>{error}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* 阶段指示器 - 横向排列 */}
      <div className="relative">
        {/* 背景连接线 */}
        <div className="absolute top-4 left-4 right-4 h-[2px] bg-slate-700/50" />
        
        {/* 进度连接线 */}
        <div 
          className="absolute top-4 left-4 h-[2px] bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 transition-all duration-700 ease-out"
          style={{ 
            width: `calc(${Math.min(currentStageIndex / (LEARNING_STAGES.length - 1) * 100, 100)}% - 2rem)` 
          }}
        />

        {/* 阶段点 */}
        <div className="flex justify-between relative">
          {LEARNING_STAGES.map((s, i) => {
            const isCompleted = i < currentStageIndex
            const isCurrent = i === currentStageIndex
            const isPending = i > currentStageIndex

            return (
              <div key={s.id} className="flex flex-col items-center z-10">
                {/* 阶段圆点 */}
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-500',
                    isCompleted && 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30',
                    isCurrent && 'bg-violet-500 text-white shadow-lg shadow-violet-500/50 animate-pulse',
                    isPending && 'bg-slate-800 text-slate-500 border border-slate-700'
                  )}
                >
                  {s.icon}
                </div>
                {/* 阶段名称 */}
                <span
                  className={cn(
                    'text-[10px] mt-1.5 font-medium transition-colors duration-300',
                    isCompleted && 'text-violet-400',
                    isCurrent && 'text-white',
                    isPending && 'text-slate-600'
                  )}
                >
                  {s.name}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* 细线进度条 */}
      <div className="space-y-2">
        {/* 进度条容器 */}
        <div className="relative h-1 bg-slate-800 rounded-full overflow-hidden">
          {/* 进度填充 */}
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          >
            {/* 发光效果 */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white/80 rounded-full blur-sm animate-pulse" />
          </div>
        </div>

        {/* 进度信息 */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-slate-400 min-w-0 flex-1">
            <span className="truncate">{message}</span>
            {subProgress && (
              <span className="text-violet-400 whitespace-nowrap">
                ({subProgress.current}/{subProgress.total})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            {isConnected && (
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            )}
            <span className="text-violet-400 font-mono font-medium tabular-nums">
              {progress}%
            </span>
          </div>
        </div>
      </div>

      {/* 当前阶段详细描述 */}
      {currentStageIndex >= 0 && currentStageIndex < LEARNING_STAGES.length && (
        <div className="text-center text-xs text-slate-500">
          {LEARNING_STAGES[currentStageIndex].description}
        </div>
      )}
    </div>
  )
}

/**
 * 紧凑版进度条 - 用于卡片或列表
 */
export function CompactLearningProgress({
  progress,
  stage,
  message,
  className,
}: {
  progress: number
  stage: string
  message?: string
  className?: string
}) {
  const stageInfo = LEARNING_STAGES.find(s => s.id === stage)

  return (
    <div className={cn('space-y-1', className)}>
      {/* 阶段 + 进度 */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-400 flex items-center gap-1">
          <span>{stageInfo?.icon || '⏳'}</span>
          <span>{stageInfo?.name || stage}</span>
        </span>
        <span className="text-violet-400 font-mono">{progress}%</span>
      </div>

      {/* 细线进度条 */}
      <div className="h-[3px] bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* 消息 */}
      {message && (
        <p className="text-[10px] text-slate-500 truncate">{message}</p>
      )}
    </div>
  )
}

export default LearningProgressBar
